import { useEffect, useMemo, useState } from 'react'
import { apiDelete, apiGet, apiPost } from '../lib/api'
import { Badge, Button, Select } from './ui'

interface TagOption {
  id: string
  key: string
  value: string
  tag_key_id: string
}

interface AssignedTag {
  id: string
  key: string
  value: string
}

interface ResourceTagsEditorProps {
  tenantId: string
  resourceId: string
  enabled: boolean
}

export function ResourceTagsEditor({ tenantId, resourceId, enabled }: ResourceTagsEditorProps) {
  const [allTags, setAllTags] = useState<TagOption[]>([])
  const [assigned, setAssigned] = useState<AssignedTag[]>([])
  const [pickKey, setPickKey] = useState('')
  const [pickTagId, setPickTagId] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!enabled || !tenantId) return
    apiGet<{ items: TagOption[] }>(`/api/v1/tenants/${tenantId}/tags?limit=500`)
      .then((d) => setAllTags(d.items ?? []))
      .catch(() => setAllTags([]))
  }, [tenantId, enabled])

  useEffect(() => {
    if (!enabled || !tenantId || !resourceId) return
    setLoading(true)
    apiGet<AssignedTag[]>(`/api/v1/tenants/${tenantId}/resources/${resourceId}/tags`)
      .then((d) => setAssigned(d ?? []))
      .catch(() => setAssigned([]))
      .finally(() => setLoading(false))
  }, [tenantId, resourceId, enabled])

  const tagKeys = useMemo(() => [...new Set(allTags.map((t) => t.key))].sort(), [allTags])
  const tagsForKey = useMemo(() => allTags.filter((t) => t.key === pickKey), [allTags, pickKey])

  const assign = async () => {
    if (!pickTagId) return
    await apiPost(`/api/v1/tenants/${tenantId}/resources/${resourceId}/tags`, { tag_id: pickTagId })
    const tag = allTags.find((t) => t.id === pickTagId)
    setAssigned((prev) => {
      if (!tag) return prev
      const multi = allTags.some((t) => t.key === tag.key && t.id !== tag.id)
      const withoutSameKey = multi
        ? prev
        : prev.filter((a) => a.key !== tag.key)
      return [...withoutSameKey, { id: tag.id, key: tag.key, value: tag.value }]
    })
    setPickTagId('')
  }

  const remove = async (tagId: string) => {
    await apiDelete(`/api/v1/tenants/${tenantId}/resources/${resourceId}/tags?tag_id=${tagId}`)
    setAssigned((prev) => prev.filter((a) => a.id !== tagId))
  }

  if (!enabled) return null

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-foreground">Tags</p>
      {loading ? (
        <p className="text-xs text-muted">Loading tags…</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {assigned.map((a) => (
            <Badge key={a.id} variant="default" className="gap-1">
              {a.key}: {a.value}
              <button type="button" className="ml-1" onClick={() => remove(a.id)}>×</button>
            </Badge>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Select className="w-32" value={pickKey} onChange={(e) => { setPickKey(e.target.value); setPickTagId('') }}>
          <option value="">Key</option>
          {tagKeys.map((k) => (
            <option key={k} value={k}>{k}</option>
          ))}
        </Select>
        <Select className="w-36" value={pickTagId} disabled={!pickKey} onChange={(e) => setPickTagId(e.target.value)}>
          <option value="">Value</option>
          {tagsForKey.map((t) => (
            <option key={t.id} value={t.id}>{t.value}</option>
          ))}
        </Select>
        <Button type="button" variant="outline" size="sm" disabled={!pickTagId} onClick={assign}>
          Add tag
        </Button>
      </div>
    </div>
  )
}
